// VIS-272: one sampler for every texture of a material that is sampled the same way (engine-wide, resort-agnostic).
//
// A WebGPU fragment stage binds at most maxSamplersPerShaderStage samplers: 16 on every adapter seen so far (sampled
// textures: the adapter's own limit, main.js asks for it, often 48). three r186 gives every filterable texture its own
// sampler binding, so Aurora's ground with the clouds on (any hour or season, phone or computer) took 17: 4 sun
// shadow maps (the far live one only where the adapter allows over 16 textures), 4 cast-light maps, 2 cloud noises,
// the ground's own 5, three's light table and the sky light. It was not drawn ("The number of samplers (17) in the
// Fragment stage exceeds the maximum per-stage limit (16)", then "Invalid BindGroupLayout" every frame).
//
// A sampler is only its settings (filters, wrapping, anisotropy, compare), so textures with the same settings can read
// through one: the same filtering, the same pixels. shareSampler(texture) marks a texture of the engine's own whose
// settings never change (and whose replacements, if any, are made the same way and marked too); sharedSamplers()
// makes three's WGSL builder bind ONE sampler per bind group and settings for the marked textures of a material's
// fragment stage (where the lit materials' maps are read):
// before the code is built, the second and later marked samplers of the same settings leave the bind group, and in the
// built WGSL their declarations go, the bindings after them move down by one, and their reads use the first one's name.
// Unmarked textures keep three's own sampler each. The WebGL2 fallback binds texture and sampler as one unit: nothing
// to share (its limit is 16 textures; the far live shadow map stays off there, shadows.js).
// A new texture read on every lit material: mark it when its settings match one already there (the sun's shadow maps,
// the cast-light maps), and keep the count: tests/qa/samplers.js (tests/e2e/qa.spec.mjs) fails a material over a limit.
import * as THREE from 'three/webgpu';
import { usable } from './internals.js'; // VIS-194: three's private parts, checked at start-up

const SHARE = new WeakSet();
/** Marks a texture whose sampling settings never change: its material reads it through a sampler shared with every
 *  other marked texture of the same settings. Returns the texture. */
export function shareSampler(texture) {
  if (texture) SHARE.add(texture);
  return texture;
}

/**
 * The built WGSL with the given samplers merged: drop = [{ name, into }] (the dropped sampler's variable, the kept one's,
 * both declared in `code`). The dropped declarations go, every later binding of the same group moves down by one (in
 * `others` too: the other stages' code, numbered on after this one), and every use in `code` reads `into`.
 * -> { code, others } (null when a declaration is not found: the build is then left as three made it)
 */
export function mergeSamplers(code, drop, others = []) {
  const at = [];
  for (const d of drop) {
    const m = code.match(new RegExp(`@binding\\( (\\d+) \\) @group\\( (\\d+) \\) var ${d.name} : sampler(_comparison)?;\\n?`));
    if (!m) return null;
    at.push({ binding: +m[1], group: +m[2] });
    code = code.replace(m[0], '');
  }
  const renumber = (c) => c.replace(/@binding\( (\d+) \) @group\( (\d+) \)/g, (all, b, g) => {
    const below = at.filter((d) => d.group === +g && d.binding < +b).length;
    return below ? `@binding( ${+b - below} ) @group( ${g} )` : all;
  });
  code = renumber(code);
  for (const d of drop) code = code.replace(new RegExp(`\\b${d.name}\\b`, 'g'), d.into);
  return { code, others: others.map((c) => (c ? renumber(c) : c)) };
}

/** Installs the shared samplers on three's WGSL builder (once; nothing when the patch point is gone). It reads three's own
 *  names in the built text: the vendored three renames them only after buildCode (scripts/vendor.mjs patchCanonicalNames).
 *  Builder = another three's (tests/unit/runtime/samplers.test.mjs: the vendored one). */
export function sharedSamplers(Builder = THREE.WGSLNodeBuilder) {
  const WB = Builder?.prototype;
  if (!WB || WB._vis272 || !usable('WGSLNodeBuilder.buildCode')) return;
  WB._vis272 = true;
  const build = WB.buildCode;
  WB.buildCode = function () {
    // before the build: the fragment stage's marked samplers, the first of each settings kept per bind group, the
    // others out of the group (getBindings() unites the stages from these lists when the build starts)
    const drop = [], out = [];
    for (const list of Object.values(this.material ? this.bindings?.fragment || {} : {})) {
      const first = new Map(); // settings -> the kept sampler binding
      for (const b of list) {
        const t = b.isSampler && !b.isSampledTexture ? b.textureNode?.value : null; // (a texture binding is a Sampler too)
        if (!t || !SHARE.has(t)) continue;
        const cmp = this.isSampleCompare(t) && b.textureNode.isSampleCompare();
        const key = `${t.minFilter}-${t.magFilter}-${t.wrapS}-${t.wrapT}-${t.wrapR || 0}-${t.anisotropy}-${t.isDepthTexture ? 1 : 0}-${cmp ? t.compareFunction : 0}`;
        const into = first.get(key);
        if (into) { drop.push({ name: b.name, into: into.name }); out.push([list, b]); } else first.set(key, b);
      }
    }
    if (!drop.length) return build.call(this);
    const at = out.map(([list, b]) => [list, list.indexOf(b), b]);
    for (const [list, b] of out) list.splice(list.indexOf(b), 1);
    const r = build.call(this);
    // after it: three still declared every sampler (getUniforms numbers from the node uniforms): merge them in the code
    const m = mergeSamplers(this.fragmentShader, drop, [this.vertexShader]);
    if (m) { this.fragmentShader = m.code; this.vertexShader = m.others[0]; return r; }
    // a declaration not where it was expected (another three): the samplers back, the bind groups as three numbered them;
    // said once, as a stage may then go over 16 samplers (2026-11-04: renamed before this ran, Aurora's ground undrawn)
    if (!WB._vis272warned) { WB._vis272warned = true; console.warn('[samplers] shared samplers not found in the built code: one sampler per texture'); }
    for (const [list, i, b] of at) list.splice(i, 0, b); // in list order: each back at its own place
    this.bindGroups = null; this.sortBindingGroups();
    return r;
  };
}
